import { useState, useEffect } from "react";

import styled from "styled-components";
import { getOwnedServers, getPublicServers } from "../../api/server";

import type { Server } from "../../types/server";

import Button from "../../Components/Button";
import Input from "../../Components/Input";
import ServerView from "../../Components/ServerView";

import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";

const Container = styled.div`
  display: flex;
  flex-flow: row nowrap;
  min-height: 0;
  max-height: 100vh;
  height: 100vh;
  padding: 28px;
  gap: 28px;
`;
const Aside = styled.aside`
  width: 384px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  padding: 20px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  position: relative;
  & > button {
    width: calc(100% - 40px);
    position: absolute;
    bottom: 20px;
  }
  gap: 32px;
`;

const FilterGroup = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  & > p {
    background: rgba(255, 255, 255, 0.32);
    height: 28px;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
    width: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #000;
    padding: 0 12px;
    text-transform: uppercase;
    border-radius: 2px 0 0 0;
  }
  & > span {
    height: 1px;
    width: 100%;
    background: rgba(255, 255, 255, 0.1);
    flex-shrink: 0;
    margin-bottom: 12px;
  }
`;
const Tags = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 12px;
  width: 100%;
  & > button {
    padding: 0 10px 0 12px;
    height: 36px;
    opacity: 0.56;
  }
`;
const Wrapper = styled.div`
  display: grid;
  grid-template-rows: 40px 1fr 40px;
  gap: 20px;
  flex-grow: 1;
  flex-shrink: 1;
  position: relative;
`;
const Header = styled.header`
  display: flex;
  flex-flow: row nowrap;
  gap: 8px;
  width: 100%;
  align-items: center;
`;
const KeyBind = styled(Button)`
  gap: 12px;
  body & {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
    padding-left: 8px;
  }
  & > span {
    border-radius: 4px;
    background: #0a0a0a;
    color: #fff;
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.2),
      0 0 0 2px rgba(255, 255, 255, 0.1);
    padding: 0 7px;
    height: 24px;
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;
const Scroller = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  overflow-y: auto;
  max-height: 100%;
  min-height: 0;
  width: 100%;
`;

export default function ServersView() {
  const [servers, setServers] = useState<Server[]>([]);
  const [owned, setOwned] = useState(false);
  useEffect(() => {
    const request = !owned
      ? getPublicServers(process.env.REACT_APP_BEARER, { limit: 16 })
      : getOwnedServers(process.env.REACT_APP_BEARER, { limit: 16 });

    Promise.resolve(request).then((result) => {
      if (!result.success) return console.error(result.error);
      console.log(result.data);
      setServers(result.data);
    });
  }, [owned]);

  return (
    <Container>
      <Aside>
        <FilterGroup>
          <p>Type</p>
          <span />
          <Tags>
            <Button
              variant={owned ? "dark" : undefined}
              onClick={() => setOwned(false)}
              disabled={!owned}
            >
              All
            </Button>
            <Button
              variant={owned ? undefined : "dark"}
              onClick={() => setOwned(true)}
              disabled={owned}
            >
              Created
            </Button>
          </Tags>
        </FilterGroup>
        <FilterGroup>
          <p>Sort By</p>
          <span />
          <Tags>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
          </Tags>
        </FilterGroup>
        <FilterGroup>
          <p>Game Modes</p>
          <span />
          <Tags>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
          </Tags>
        </FilterGroup>
        <FilterGroup>
          <p>Popular Tags</p>
          <span />
          <Tags>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
            <Button variant="dark">Filter</Button>
          </Tags>
        </FilterGroup>
        <Button disabled>reset filters</Button>
      </Aside>
      <Wrapper>
        <Header>
          <Input
            placeholder="Search..."
            style={{
              flexGrow: 1,
            }}
            icon={<SearchIcon />}
          />
          <Button>Create Server</Button>
        </Header>
        <Scroller>
          {servers.map((server) => (
            <ServerView key={server.id} server={server} />
          ))}
        </Scroller>
        <Row style={{ justifyContent: "flex-end" }}>
          <KeyBind>
            <span>esc</span>
            back
          </KeyBind>
        </Row>
      </Wrapper>
    </Container>
  );
}
